import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { CreatePasswordForm } from 'components/CreatePassword/CreatePassword';
import FormError from 'components/Forms/FormError';
import FormInput from 'components/Forms/FormInput';
import { UniqueHashTypeEnum, useRequestOrganisationCustomerPasswordResetEmailMutation } from 'generated/graphql';
import { useServerTranslation } from 'hooks/useServerTranslation';

import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

type ResetPasswordProps = {
    email?: string;
    hash?: string;
};

export default function ResetPassword({ email, hash }: ResetPasswordProps) {
    const [sent, setSent] = useState(false);
    const router = useRouter();
    if (email && hash) {
        return (
            <CreatePasswordForm
                hashType={UniqueHashTypeEnum.ResetPassword}
                email={email}
                hash={hash}
                onComplete={() => router.push('/user/login')}
            />
        );
    }

    return (
        <Box>
            {
                sent
                    ? <Sent />
                    : <ResetForm setSent={setSent} />
            }
        </Box>
    );
}

function ResetForm({ setSent }: { setSent: (sent: boolean) => void }) {
    const { t } = useServerTranslation();
    const resetFormSchema = z.object({
        email: z.string()
            .min(1, { message: t('errors.required.email') })
            .email({ message: t('errors.format.email') }),
    });

    const [resetPassword, { data, loading, error }] = useRequestOrganisationCustomerPasswordResetEmailMutation();

    type ResetFormType = z.infer<typeof resetFormSchema>;

    const methods = useForm<ResetFormType>({ resolver: zodResolver(resetFormSchema) });
    const { handleSubmit } = methods;
    const sendCode = async ({ email }: ResetFormType) => {
        await resetPassword({ variables: { email } });
    };

    useEffect(() => {
        if (data?.requestOrganisationCustomerPasswordResetEmail.success) {
            setSent(true);

            return;
        }
        setSent(false);
    }, [data, setSent]);

    const couldNotSendResetPassword = data?.requestOrganisationCustomerPasswordResetEmail.success === false;

    return (
        <Box paddingBottom={2}>
            <Typography
                variant="h2"
                paddingBottom={2}
            >
                {t('user.reset-password-explanation')}
            </Typography>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(sendCode)}>
                    <FormInput
                        label={t('form-fields.user.email')}
                        name="email"
                        type="email"
                        variant="outlined"
                        size="small"
                    />
                    <Box
                        display="flex"
                        justifyContent="end"
                        paddingTop={6}
                        paddingBottom={1}
                    >
                        <Button
                            disabled={loading}
                            variant="darkRed"
                            type="submit"
                        >
                            {t('user.reset-password-send')}
                        </Button>
                    </Box>
                    {loading ? <LinearProgress /> : null}
                    <Box display="flex" justifyContent="flex-end">
                        <FormError show={couldNotSendResetPassword || Boolean(error?.message)}>
                            {t('user.reset-password-error')}
                        </FormError>
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}

function Sent() {
    const { t } = useServerTranslation();

    return <>{t('user.reset-password-sent')}</>;
}
